import { useState } from 'react';
import { ChevronRight, ChevronDown } from 'lucide-react';
import type { Schema, OpenApiSpec } from '@/lib/openapi/types';
import { resolveSchema } from '@/lib/openapi/parse';

interface Props {
    schema: Schema;
    spec: OpenApiSpec;
    depth?: number;
}

export default function SchemaViewer({ schema, spec, depth = 0 }: Props) {
    const resolved = resolveSchema(spec, schema);
    if (!resolved) return null;
    return <Node schema={resolved} spec={spec} depth={depth} />;
}

function Node({ schema, spec, depth }: { schema: Schema; spec: OpenApiSpec; depth: number }) {
    const resolved = resolveSchema(spec, schema);

    if (resolved.type === 'object' && resolved.properties) {
        return (
            <div className="space-y-1.5">
                {Object.entries(resolved.properties).map(([name, prop]) => {
                    const propResolved = resolveSchema(spec, prop);
                    const required = resolved.required?.includes(name);
                    return (
                        <PropertyRow key={name} name={name} prop={propResolved} required={required} spec={spec} depth={depth} />
                    );
                })}
            </div>
        );
    }

    if (resolved.type === 'array' && resolved.items) {
        const itemsResolved = resolveSchema(spec, resolved.items);
        return (
            <div className="py-1">
                <span className="text-xxs text-ghost mr-2">Array of:</span>
                <Node schema={itemsResolved} spec={spec} depth={depth + 1} />
            </div>
        );
    }

    return null;
}

function PropertyRow({ name, prop, required, spec, depth }: { name: string; prop: Schema; required?: boolean; spec: OpenApiSpec; depth: number }) {
    const [open, setOpen] = useState(depth === 0);
    const hasChildren = (prop.type === 'object' && prop.properties) || (prop.type === 'array' && prop.items);

    return (
        <div style={{ paddingLeft: depth * 12 }}>
            <div className="flex items-start gap-2 py-1.5">
                {hasChildren && (
                    <button onClick={() => setOpen((v) => !v)} className="text-ghost hover:text-muted mt-0.5 shrink-0">
                        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                    </button>
                )}
                {!hasChildren && <span className="w-3 shrink-0" />}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <code className="text-xs font-semibold text-ink font-mono">{name}</code>
                        <span className="text-xxs text-ghost font-mono">{typeOf(prop)}</span>
                        {required && <span className="text-xxs font-semibold text-red-400">wajib</span>}
                        {prop.nullable && <span className="text-xxs text-ghost">nullable</span>}
                    </div>
                    {prop.description && <p className="text-xxs text-muted mt-0.5">{prop.description}</p>}
                    {prop.example !== undefined && (
                        <code className="text-xxs text-ghost mt-1 block font-mono">contoh: {JSON.stringify(prop.example)}</code>
                    )}
                    {prop.enum && (
                        <p className="text-xxs text-ghost mt-1">
                            {prop.enum.map((v) => <code key={String(v)} className="font-mono mr-1.5">{`"${v}"`}</code>)}
                        </p>
                    )}
                </div>
            </div>
            {open && hasChildren && (
                <div className="ml-3 pl-3" style={{ borderLeft: '1px solid var(--border)' }}>
                    <Node schema={prop} spec={spec} depth={depth + 1} />
                </div>
            )}
        </div>
    );
}

function typeOf(p: Schema): string {
    if (p.format) return `${p.type}<${p.format}>`;
    if (p.type === 'array' && p.items) return `${p.items.type || 'any'}[]`;
    return p.type || 'any';
}
